import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { GetApp as GetAppIcon, PhoneIphone as PhoneIcon } from '@mui/icons-material';
import { usePwaInstall } from '../hooks/use-pwa-install';
import PWAInstallGuideDialog from './PWAInstallGuideDialog';

/**
 * Install control for the login screen (and similar entry points).
 * @param {'default' | 'footer'} variant - default: full button; footer: QR-style card for login footer
 */
export default function PWAInstallButton({ fullWidth = true, variant = 'default', sx = {} }) {
  const { isStandalone, canNativeInstall, platform, promptInstall, showInstallOption } = usePwaInstall();
  const [guideOpen, setGuideOpen] = useState(false);

  if (!showInstallOption) return null;

  const handleClick = async () => {
    if (platform.isInAppBrowser) {
      setGuideOpen(true);
      return;
    }
    if (canNativeInstall) {
      const { outcome } = await promptInstall();
      if (outcome === 'accepted') return;
      setGuideOpen(true);
      return;
    }
    setGuideOpen(true);
  };

  const label = platform.isIOS
    ? 'Add to Home Screen'
    : platform.isAndroid
      ? 'Install on this phone'
      : 'Install Galldris Core';

  const helperText = platform.isDesktop
    ? 'Step-by-step guide for Chrome and Edge · Opens like a normal app'
    : 'Works on phone and tablet · Opens from your home screen · No app store needed';

  if (variant === 'footer') {
    return (
      <>
        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            py: 1,
            px: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: 'rgba(17, 87, 64, 0.06)',
            textAlign: 'left',
            ...sx
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#fff',
              borderRadius: 0.75,
              border: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main'
            }}
          >
            {platform.isIOS ? (
              <PhoneIcon sx={{ fontSize: '1.75rem' }} />
            ) : (
              <GetAppIcon sx={{ fontSize: '1.75rem' }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, display: 'block', lineHeight: 1.3, mb: platform.isDesktop ? 0 : 0.5 }}
            >
              {label}
            </Typography>
            {platform.isDesktop && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, mb: 0.5 }}>
                {helperText}
              </Typography>
            )}
            <Button
              type="button"
              size="small"
              variant="text"
              onClick={handleClick}
              startIcon={
                platform.isIOS ? (
                  <PhoneIcon sx={{ fontSize: '0.95rem !important' }} />
                ) : (
                  <GetAppIcon sx={{ fontSize: '0.95rem !important' }} />
                )
              }
              sx={{
                color: 'primary.main',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                p: 0,
                minHeight: 0,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
              }}
            >
              Install
            </Button>
          </Box>
        </Box>
        <PWAInstallGuideDialog
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
          platform={platform}
        />
      </>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, ...sx }}>
        <Button
          fullWidth={fullWidth}
          variant="outlined"
          size="large"
          startIcon={platform.isIOS ? <PhoneIcon /> : <GetAppIcon />}
          onClick={handleClick}
          sx={{
            py: 1.25,
            fontSize: '0.95rem',
            fontWeight: 600,
            textTransform: 'none',
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              bgcolor: 'rgba(17, 87, 64, 0.06)'
            }
          }}
        >
          {label}
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          {helperText}
        </Typography>
        {platform.isDesktop && (
          <Typography
            variant="caption"
            color="primary.main"
            sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontWeight: 600 }}
          >
            Tap the button above for pictured steps
          </Typography>
        )}
      </Box>

      <PWAInstallGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        platform={platform}
      />
    </>
  );
}
